import React, { useEffect } from 'react';
import { CircularProgress, Tab, Tabs, Grid } from '@material-ui/core';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { useFormikContext, getIn } from 'formik';
import { rejectCodesToBeClearedInStaff } from '@ourbranch/lookups';

import { Card } from 'core/components/card';
import { useStore } from 'core/store/store.mobx';
import { OfferPageTabs } from 'core/store/offer-store';
import { ValidationErrorsModal, useValidationErrorsModal } from 'core/components/validation-errors-modal';
import { OfferNotifications } from 'offer/components/notifications';
import { AutoDetails } from './components/auto-details';
import { OfferDialog, CustomerSearchDialog, SearchReferralDialog } from './components/dialog';
import { Footer } from './components/footer';
import { HomeDetails } from './components/home-details';
import { PeopleDetails } from './components/people-details';
import { Settings } from './components/settings';
import PipMI from 'common/components/pip';
import Renters from 'common/components/auto/renters/renters';
import Notification from 'core/helpers/notifications';
import ConnectedHomeDiscountModal from './components/sidebar/components/discounts-card/connected-home-discount/connected-home-discount-modal';

import useStyles from './offer.styles';
import { useToast } from 'core/components/toast';

const Offer = observer(() => {
  const classes = useStyles();
  const toast = useToast();
  const { offer: store } = useStore();
  const { dirty, values, validateForm } = useFormikContext();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();

  const list = getIn(values, 'drivers') ?? [];
  const enableApplicantSwap = list.some((item) => item.isCoApplicant === true);
  const notificationTypes = store?.notifications?.map((i) => i).reduce((curr, prev) => prev.concat(curr));
  const shouldClearRejectCodes = rejectCodesToBeClearedInStaff.includes(
    store.offer?.quote?.offerings?.homeownersRejectCode
  );

  useEffect(() => {
    store.setFormAction({ dirty, stale: store.isStale });
    // if the validation schema changes *after* the validation has run we have call validate again manually
    validateForm();
  }, [dirty, store, validateForm, store.isStale]);

  useEffect(() => {
    // auto scroll up so notifications are in view
    if (
      store.notifications?.length &&
      notificationTypes !== Notification.Offer.NewConnectedHomeSignUpCustomer &&
      !store.loading
    ) {
      document.getElementById('notificationsContainer').scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [store.notifications, store.loading, notificationTypes]);

  // Clear whitelisted reject codes if exists
  useEffect(() => {
    const clearRejectCodes = async () => {
      await store.clearRejectCodes();
      if (store.areRejectCodesCleared) {
        toast.notify({
          type: 'success',
          message:
            'This offer is for a home located in a wind/hail exclusion zone. Please ensure the member understands what this means for their coverage, and assist them with purchasing the excluded coverage through one of our partners, if applicable.',
          durationInSeconds: 15,
          label: 'X',
          action: () => {}
        });
      }
    };

    if (shouldClearRejectCodes) {
      clearRejectCodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldClearRejectCodes, store.areRejectCodesCleared]);

  return (
    <>
      <div
        className={classNames(
          { [classes.container]: store.errors.length === 0 },
          { [classes.withErrors]: store.errors.length > 0 }
        )}
      >
        {store.notifications?.length && notificationTypes !== 'NewConnectedHomeSignUpCustomer' && (
          <OfferNotifications />
        )}
        {store.showConectedHomeModal && <ConnectedHomeDiscountModal />}
        <Tabs value={store.currentTab} variant="fullWidth" onChange={(_, newTab) => store.setCurrentTab(newTab)}>
          <Tab value={OfferPageTabs.PEOPLE} label="People" className={classes.tab} />
          {store.shouldShowHomeTab && <Tab value={OfferPageTabs.HOME} label="Home Details" className={classes.tab} />}
          {store.shouldShowAutoTab && <Tab value={OfferPageTabs.AUTO} label="Auto Details" className={classes.tab} />}
          {store.shouldShowRentersTab && (
            <Tab value={OfferPageTabs.RENTERS} label="Renters Details" className={classes.tab} />
          )}
          {store.shouldShowPIPTab && <Tab value={OfferPageTabs.PIP} label="PIP" className={classes.tab} />}
          <Tab value={OfferPageTabs.SETTINGS} label="Settings" className={classes.tab} />
        </Tabs>
        {(store.loading || !store.offer) && (
          <Card className={classes.cardLoadingContainer}>
            <Grid container className={classes.loaderContainer} alignItems="center" justify="center">
              <CircularProgress className={classes.loader} />
            </Grid>
          </Card>
        )}
        {store.offer && !store.loading && (
          <>
            {store.currentTab === OfferPageTabs.SETTINGS && <Settings />}
            {store.currentTab === OfferPageTabs.PEOPLE && (
              <PeopleDetails enableApplicantSwap={enableApplicantSwap} hasAuto={store.shouldShowAutoTab} />
            )}
            {store.currentTab === OfferPageTabs.AUTO && <AutoDetails />}
            {store.currentTab === OfferPageTabs.HOME && <HomeDetails />}
            {store.currentTab === OfferPageTabs.RENTERS && <Renters />}
            {store.currentTab === OfferPageTabs.PIP && <PipMI />}
            {store.openDialog && <OfferDialog onClose={() => store.triggerOfferDialog(false)} />}
            <CustomerSearchDialog />
            {!store.offer.quote?.existingCustomer && <SearchReferralDialog />}
          </>
        )}
        {store.offer && (
          <Footer
            showValidationErrorsModal={showValidationErrorsModal}
            setShowValidationErrorsModal={setShowValidationErrorsModal}
          />
        )}
        <ValidationErrorsModal open={showValidationErrorsModal} onClose={() => setShowValidationErrorsModal(false)} />
      </div>
    </>
  );
});

export default Offer;
